import { Users } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { ModuleClientOverviewDto } from "../api/types";
import { useModuleClients } from "../api/useModuleClients";
import { useIntegrationModuleDetailBreadcrumbs } from "../hooks/useIntegrationModuleDetailBreadcrumbs";
import { WebhookTabWrapper } from "../webhooks/components/WebhookTabWrapper";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Badge } from "@/components/ui/v2/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/v2/select";
import { DataTable } from "@/components/ui/v2/table/data-table";
import { TableToolbar } from "@/components/ui/v2/table/table-toolbar";
import { ROUTES } from "@/constants/routes";
import { useIntegrationModule } from "@/features/integrations/hooks/useIntegrationModule";
import { useModuleConfigurationSchema } from "@/features/integrations/hooks/useModuleConfiguration";
import {
  getActionHandlers,
  renderComponent,
} from "@/features/integrations/shared/renderer";
import type { UiComponent } from "@/features/integrations/types";
import { decodeSlug } from "@/features/integrations/utils/slug";
import { useAuth } from "@/lib/hooks/useAuth";
import { useModuleStatusFilterSearchParams } from "@/lib/hooks/useModuleStatusFilterSearchParams";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";
import useToast from "@/lib/hooks/useToast";
import { TableColumn } from "@/types/table/tableProps";

type RouteParams = {
  integrationName?: string;
  moduleName?: string;
};

function TenantModuleDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { integrationName: integrationNameParam, moduleName: moduleNameParam } =
    useParams<RouteParams>();
  const { tenantId } = useAuth();
  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
  } = useSearchSearchParams();
  const { moduleStatus, setModuleStatus } = useModuleStatusFilterSearchParams();
  const { orderBy, descending } = useSortSearchParams();
  const handleSort = useTableSortHandler<ModuleClientOverviewDto>();

  const decodedIntegrationName = integrationNameParam
    ? decodeSlug(integrationNameParam)
    : null;
  const backendIntegrationName = decodedIntegrationName ?? "";

  const decodedModuleName = moduleNameParam ? decodeSlug(moduleNameParam) : "";

  const { breadcrumbs, integration } =
    useIntegrationModuleDetailBreadcrumbs(decodedModuleName);

  const { data: moduleData } = useIntegrationModule({
    integrationName: backendIntegrationName,
    moduleName: decodedModuleName,
    tenantId: tenantId ?? undefined,
  });

  const moduleStatusFilter =
    moduleStatus === "all"
      ? null
      : moduleStatus === "active"
        ? "active"
        : "inactive";

  const { data, isLoading, isFetching, refetch } = useModuleClients({
    tenantId: tenantId ?? "",
    integrationName: backendIntegrationName,
    moduleName: decodedModuleName,
    page,
    pageSize,
    search: debouncedSearchValue,
    moduleStatus: moduleStatusFilter,
    orderBy: orderBy ?? null,
    descending: descending ?? null,
  });

  const clients = useMemo(() => data?.data ?? [], [data?.data]);
  const totalRecords = data?.totalRecords ?? 0;

  const handleRowClick = useCallback(
    (client: ModuleClientOverviewDto) => {
      if (integration) {
        navigate(
          `/${ROUTES.clients}/${client.clientId}/${ROUTES.integrations}/${integration.name}/${decodedModuleName}`,
        );
      }
    },
    [integration, navigate, decodedModuleName],
  );

  // Schema-based UI rendering (for integrations with configured views)
  const schemaQuery = useModuleConfigurationSchema(
    backendIntegrationName.toLowerCase(),
    decodedModuleName,
    { level: "tenant" as const, tenantId: tenantId ?? null },
    !!backendIntegrationName,
  );
  const schemaUi: UiComponent[] = useMemo(
    () => schemaQuery.data?.ui ?? [],
    [schemaQuery.data],
  );

  const actionHandlers = useMemo(
    () => getActionHandlers(backendIntegrationName),
    [backendIntegrationName],
  );

  const executeAction = useCallback(
    async (
      actionKey: string,
      messages: { successMessage: string; errorMessage: string },
    ) => {
      const handler = actionHandlers[actionKey];
      if (!handler) return;
      try {
        await handler({
          integrationName: backendIntegrationName,
          moduleName: decodedModuleName,
          tenantId: tenantId ?? undefined,
          onSuccess: () => {
            if (messages.successMessage) {
              showToast(t(messages.successMessage), "success");
            }
            refetch();
          },
          onError: (error) => {
            showToast(
              messages.errorMessage ? t(messages.errorMessage) : error.message,
              "error",
            );
          },
        });
      } catch (error) {
        showToast(
          messages.errorMessage
            ? t(messages.errorMessage)
            : error instanceof Error
              ? error.message
              : String(error),
          "error",
        );
      }
    },
    [
      actionHandlers,
      backendIntegrationName,
      decodedModuleName,
      tenantId,
      showToast,
      t,
      refetch,
    ],
  );

  const handleSortWrapper = useCallback(
    (accessor: string | undefined, direction: string | null) => {
      if (!accessor) return;
      handleSort(
        accessor as keyof ModuleClientOverviewDto,
        direction as "asc" | "desc" | null,
      );
    },
    [handleSort],
  );

  const context = useMemo(
    () => ({
      integrationName: backendIntegrationName,
      moduleName: decodedModuleName,
      tenantId: tenantId ?? undefined,
      executeAction,
      tableData: clients,
      isLoading: isLoading || isFetching,
      pagination: {
        page,
        pageSize,
        totalRecords,
        onPageChange: setPage,
        onPageSizeChange: setPageSize,
      },
      onSort: handleSortWrapper,
      onRowClick: (row: unknown) =>
        handleRowClick(row as ModuleClientOverviewDto),
      orderBy: orderBy ?? null,
      descending: descending ?? false,
      emptyMessage: t("common.noResultsFound"),
      searchValue: searchValue ?? "",
      onSearchChange: setSearchValue,
      filterValues: { moduleStatus: moduleStatus ?? "all" },
      onFilterChange: (key: string, value: unknown) => {
        if (key === "moduleStatus")
          setModuleStatus(value as "all" | "active" | "inactive");
      },
    }),
    [
      backendIntegrationName,
      decodedModuleName,
      tenantId,
      executeAction,
      clients,
      isLoading,
      isFetching,
      page,
      pageSize,
      totalRecords,
      setPage,
      setPageSize,
      handleSortWrapper,
      handleRowClick,
      orderBy,
      descending,
      searchValue,
      setSearchValue,
      moduleStatus,
      setModuleStatus,
      t,
    ],
  );

  const defaultColumns: TableColumn<ModuleClientOverviewDto>[] = useMemo(
    () => [
      {
        header: t("common.clientName"),
        accessor: "clientName",
        width: "50%",
      },
      {
        header: t("common.status"),
        accessor: "moduleStatus",
        width: "50%",
        Cell: (item) => (
          <Badge
            variant={item.moduleStatus ? "success" : "neutral"}
            size="small"
          >
            {item.moduleStatus ? t("common.active") : t("common.inactive")}
          </Badge>
        ),
      },
    ],
    [t],
  );

  const renderContent = () => {
    if (schemaUi.length > 0) {
      return (
        <div className="space-y-4">
          {schemaUi.map((component, index) => (
            <div key={`${component.component}-${index}`}>
              {renderComponent(component, context)}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <TableToolbar
            onSearchChange={setSearchValue}
            searchValue={searchValue}
            searchPlaceholder={t("common.search")}
          />
          <Select
            value={moduleStatus ?? "all"}
            onValueChange={(value) =>
              setModuleStatus(value as "all" | "active" | "inactive")
            }
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder={t("common.status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")}</SelectItem>
              <SelectItem value="active">{t("common.active")}</SelectItem>
              <SelectItem value="inactive">{t("common.inactive")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <DataTable
          columns={defaultColumns}
          data={clients}
          isLoading={isLoading || isFetching}
          onRowClick={handleRowClick}
          onSort={handleSortWrapper}
          orderBy={orderBy ?? undefined}
          descending={descending ?? false}
          page={page}
          pageSize={pageSize}
          totalRecords={totalRecords}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          getRowId={(row) => row.clientId}
          emptyState={{
            icon: Users,
            title: t("common.noResultsFound"),
          }}
        />
      </div>
    );
  };

  const moduleDisplayName = t(`integrations.modules.names.${decodedModuleName}`);
  const backHref = integrationNameParam
    ? `/${ROUTES.integrations}/${integrationNameParam}`
    : `/${ROUTES.integrations}`;

  return (
    <ModernPageLayout
      breadcrumbs={breadcrumbs}
      title={moduleDisplayName}
      subtitle={t(`integrations.modules.descriptions.${decodedModuleName}`, { defaultValue: "" }) || undefined}
      backbutton={{ label: t("common.back"), href: backHref }}
    >
      <WebhookTabWrapper
        integrationId={moduleData?.tenantIntegrationId}
        scopes={moduleData?.scopes}
      >
        {renderContent()}
      </WebhookTabWrapper>
    </ModernPageLayout>
  );
}

export default TenantModuleDetailPage;
